<template>
  <div>
    <el-dialog
      width="80%"
      :visible="dialogVisible"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CampaignForm_RecipientsListDialog_Title') }}
        </div>
      </div>
      <div class="items-container" v-loading="loading">
        <div
          class="no-results-found-text"
          v-if="!loading && !Object.keys(messageTypes).length"
        >
          {{ $t('CampaignForm_RecipientsListDialog_NoData') }}
        </div>
        <el-tabs class="tabs" :stretch="true" v-model="activeTab">
          <el-tab-pane
            v-for="(item, key) in messageTypes"
            :label="getChannelLabel(key)"
            :name="key"
            :key="key"
          />
        </el-tabs>
        <rest-list
          v-if="
            messageTypes[activeTab] && messageTypes[activeTab].profile_count
          "
          :payload="fetchEndpointProfiles"
          :key="activeTab"
        >
          <rest-list-column
            v-if="selectedColumnsProfile.id"
            prop="id"
            :title="$t('UserProfiles_Field_Id')"
            :route="rowClickRoute"
            :min-width="280"
            :new-tab="true"
          />
          <rest-list-column-enum
            v-if="selectedColumnsProfile.identifier"
            prop="identifier"
            :enum="profileIdentifiersLabels"
            :title="$t('UserProfiles_Field_Identifier')"
            :min-width="120"
          />
          <rest-list-column
            v-if="selectedColumnsProfile.customer_id && customerIdEnabled"
            prop="customer_id"
            :title="$t('UserProfiles_Field_CustomerId')"
            :min-width="180"
          />
          <rest-list-column
            v-if="selectedColumnsProfile.user_id"
            prop="user_id"
            :title="$t('UserProfiles_Field_UserId')"
            :min-width="180"
          />
          <rest-list-column
            v-if="selectedColumnsProfile.email"
            prop="email"
            :title="$t('UserProfiles_Field_Email')"
            :min-width="180"
          />
          <rest-list-column
            v-if="selectedColumnsProfile.mobile_number"
            prop="mobile_number"
            :title="$t('UserProfiles_Field_MobileNumber')"
            :min-width="180"
          />
          <rest-list-column
            v-if="selectedColumnsProfile.whatsapp_number"
            prop="whatsapp_number"
            :title="$t('UserProfiles_Field_WhatsappNumber')"
            :min-width="180"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.push_subscription"
            prop="push_subscription"
            :title="$t('UserProfiles_Field_PushSubscription')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.email_addressable"
            prop="email_addressable"
            :title="$t('UserProfiles_Field_EmailAddressable')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.email_subscription"
            prop="email_subscription"
            :title="$t('UserProfiles_Field_EmailSubscription')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.sms_addressable"
            prop="sms_addressable"
            :title="$t('UserProfiles_Field_SmsAddressable')"
            :min-width="140"
          />

          <rest-list-column-boolean
            v-if="selectedColumnsProfile.sms_subscription"
            prop="sms_subscription"
            :title="$t('UserProfiles_Field_SmsSubscription')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.whatsapp_addressable"
            prop="whatsapp_addressable"
            :title="$t('UserProfiles_Field_WhatsappAddressable')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.whatsapp_subscription"
            prop="whatsapp_subscription"
            :title="$t('UserProfiles_Field_WhatsappSubscription')"
            :min-width="140"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsProfile.archived"
            prop="merge_to"
            :title="$t('UserProfiles_Field_Archived')"
            :min-width="140"
          />
          <rest-list-column
            v-for="item in profileAttributes"
            v-if="selectedColumnsProfile[`attr_${item.name}`]"
            :prop="`custom_attributes.${item.name}`"
            :title="item.display_name || item.name"
            :key="item.name"
            :min-width="140"
            :filter-input="false"
            :sortable="false"
          />
          <el-table-column :width="50" align="center">
            <template slot="header" slot-scope="{}">
              <rest-list-column-settings
                :storage-key="`recipients-list-profile-columns-${activeTab}`"
                :title="$t('Devices_Field_Settings')"
                :columns="profileColumns"
                v-model="selectedColumnsProfile"
              />
            </template>
          </el-table-column>
        </rest-list>
        <rest-list
          v-if="messageTypes[activeTab] && messageTypes[activeTab].device_count"
          :payload="fetchEndpointDevices"
          :key="activeTab"
        >
          <rest-list-column
            v-if="selectedColumnsDevice.id"
            prop="id"
            :title="$t('Devices_Field_Id')"
            :route="rowClickDeviceIdRoute"
            :new-tab="true"
            :min-width="120"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.profile_id"
            prop="profile_id"
            :title="$t('Devices_Field_ProfileId')"
            :route="rowClickProfileIdRoute"
            :new-tab="true"
            :min-width="300"
          />
          <rest-list-column
            v-if="
              selectedColumnsDevice.user_id && $can('UserPersonalData::Read')
            "
            prop="user_id"
            :title="$t('Devices_Field_UserId')"
            :min-width="120"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.external_id"
            prop="external_id"
            :title="$t('Devices_Field_ExternalId')"
            :min-width="120"
          />
          <rest-list-column-enum
            v-if="selectedColumnsDevice.application"
            prop="application_id"
            :title="$t('Devices_Field_Application')"
            :enum="applicationFilter"
            :min-width="150"
            :translate="false"
          />
          <rest-list-column-enum
            v-if="selectedColumnsDevice.type"
            prop="type"
            :title="$t('Devices_Field_Type')"
            :enum="deviceTypeFilter"
            :min-width="150"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsDevice.active"
            prop="active"
            :title="$t('Devices_Field_Active')"
            :min-width="150"
          />
          <rest-list-column-enum
            v-if="selectedColumnsDevice.environment"
            prop="environment"
            :title="$t('Devices_Field_Environment')"
            :enum="pushEnvironments"
            :min-width="150"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsDevice.addressable"
            prop="addressable"
            :title="$t('Devices_Field_Addressable')"
            :min-width="150"
          />
          <rest-list-column-boolean
            v-if="selectedColumnsDevice.subscription"
            prop="subscription"
            :title="$t('Devices_Field_Subscription')"
            :min-width="150"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.token"
            prop="token"
            :title="$t('Devices_Field_Token')"
            :min-width="300"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.device_model_name"
            prop="device_model_name"
            :title="$t('Devices_Field_DeviceModelName')"
            :min-width="150"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.browser"
            prop="browser"
            :title="$t('Devices_Field_Browser')"
            :min-width="150"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.name"
            prop="name"
            :title="$t('Devices_Field_Name')"
            :min-width="150"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.device_id"
            prop="device_id"
            :title="$t('Devices_Field_DeviceId')"
            :min-width="300"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.device_idfv"
            prop="device_idfv"
            :title="$t('Devices_Field_IDFV')"
            :min-width="300"
          />

          <rest-list-column
            v-if="selectedColumnsDevice.device_idfa"
            prop="device_idfa"
            :title="$t('Devices_Field_IDFA')"
            :min-width="300"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.device_anid"
            prop="device_anid"
            :title="$t('Devices_Field_ANID')"
            :min-width="300"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.device_adid"
            prop="device_adid"
            :title="$t('Devices_Field_ADID')"
            :min-width="300"
          />
          <rest-list-column
            v-if="selectedColumnsDevice.ga_id"
            prop="ga_id"
            :title="$t('Devices_Field_GaID')"
            :min-width="150"
          />
          <rest-list-column-date
            v-if="selectedColumnsDevice.create_time"
            prop="create_time"
            :title="$t('Devices_Field_CreateTime')"
            :width="200"
          />
          <rest-list-column-date
            v-if="selectedColumnsDevice.deactivate_time"
            prop="deactivate_time"
            :title="$t('Devices_Field_DeactivateTime')"
            :width="200"
          />
          <rest-list-column-date
            v-if="selectedColumnsDevice.open_time"
            prop="open_time"
            :title="$t('Devices_Field_OpenTime')"
            :width="200"
          />
          <rest-list-column
            v-for="item in allAttributes"
            v-if="selectedColumnsDevice[`attr_${item.name}`]"
            :prop="`attributes.${item.name}`"
            :title="item.display_name || item.name"
            :key="item.name"
            :min-width="140"
            :filter-input="false"
            :sortable="false"
          >
            <template slot-scope="{ row }">
              {{
                row.device_attributes[item.name]
                  ? row.device_attributes[item.name]
                  : row.profile_attributes[item.name] || '-'
              }}
            </template>
          </rest-list-column>
          <el-table-column :width="50" align="center">
            <template slot="header" slot-scope="{}">
              <rest-list-column-settings
                storage-key="recipients-list-device-columns"
                :title="$t('Devices_Field_Settings')"
                :columns="deviceColumns"
                v-model="selectedColumnsDevice"
              />
            </template>
          </el-table-column>
        </rest-list>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListColumn from '@/common/components/rest/RestListColumn'
import { profileIdentifiersLabels } from '@/modules/profiles/enum/ProfileIdentifiersEnum'
import { DeviceTypeLabel } from '@/modules/devices/enum/DeviceTypeEnum'
import { PushEnvironmentLabel } from '@/common/enum/PushEnvironmentEnum'
import {
  channelLabels,
  channelTypes
} from '@/modules/campaign-manage/enum/Labels'
import RestApi from '@/core/services/RestApi'
import _ from 'lodash'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'RecipientsListDialog',
  components: {
    RestListColumnDate,
    RestListColumnEnum,
    RestListColumnBoolean,
    RestListColumnSettings,
    RestListColumn,
    RestList
  },
  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    taskId: {
      type: Number,
      required: true
    },
    model: {
      required: true
    }
  },
  mounted() {
    this.loading = true
    Promise.all([
      RestApi(this.fetchAttributes)
        .then(({ data }) => {
          this.allAttributes = data
          this.profileAttributes = data.filter(item => item.attribute_profile)
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        }),
      new RestApiPaginator(this.appsEndpoint)
        .fetchAllPages()
        .then(data => {
          this.allApplications = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    ]).then(res => {
      RestApi(this.fetchMessageTypes)
        .then(({ data }) => {
          this.loading = false
          this.messageTypes = data.messageTypes
          this.activeTab = Object.keys(data.messageTypes)[0]
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    })
  },
  data() {
    return {
      loading: false,
      activeTab: null,
      messageTypes: {},
      selectedColumnsProfile: {},
      selectedColumnsDevice: {},
      channelTypes,
      channelLabels,
      profileIdentifiersLabels,
      profileAttributes: [],
      allAttributes: [],
      allApplications: [],
      DeviceTypeLabel,
      PushEnvironmentLabel
    }
  },
  computed: {
    fetchMessageTypes() {
      return projectEndpoint({
        method: 'get',
        url: `campaign-previews/message-types-count`,
        params: { task_id: this.taskId }
      })
    },
    fetchEndpointProfiles() {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: {
          sort: '-id',
          task_id: this.taskId,
          message_type: this.activeTab,
          expand: 'custom_attributes',
          'per-page': 10
        }
      })
    },
    fetchEndpointDevices() {
      return projectEndpoint({
        method: 'get',
        url: `devices`,
        params: {
          sort: '-id',
          task_id: this.taskId,
          message_type: this.activeTab,
          expand: 'device_attributes,profile_attributes',
          'per-page': 10
        }
      })
    },
    fetchAttributes() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: {
          attribute_profile_or_client: 1,
          sort: 'title',
          limit: '-'
        }
      })
    },
    appsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'applications',
        params: {}
      })
    },
    features() {
      return this.$store.state.app.features
    },
    profileColumns() {
      const columns = {
        id: {
          title: this.$t('UserProfiles_Field_Id'),
          defaultValue: true,
          validation: true
        },
        identifier: {
          title: this.$t('UserProfiles_Field_Identifier'),
          defaultValue: false,
          validation: true
        },
        customer_id: {
          title: this.$t('UserProfiles_Field_CustomerId'),
          defaultValue: false,
          validation: this.customerIdEnabled
        },
        user_id: {
          title: this.$t('UserProfiles_Field_UserId'),
          defaultValue: true,
          validation: this.$can('UserPersonalData::Read')
        },
        email: {
          title: this.$t('UserProfiles_Field_Email'),
          defaultValue: parseInt(this.activeTab) === channelTypes.email,
          validation: this.$can('UserPersonalData::Read')
        },
        mobile_number: {
          title: this.$t('UserProfiles_Field_MobileNumber'),
          defaultValue: parseInt(this.activeTab) === channelTypes.sms,
          validation: this.$can('UserPersonalData::Read')
        },
        whatsapp_number: {
          title: this.$t('UserProfiles_Field_WhatsappNumber'),
          defaultValue: false,
          validation:
            this.features.whatsapp && this.$can('UserPersonalData::Read')
        },
        push_subscription: {
          title: this.$t('UserProfiles_Field_PushSubscription'),
          defaultValue: false,
          validation: true
        },
        email_addressable: {
          title: this.$t('UserProfiles_Field_EmailAddressable'),
          defaultValue: false,
          validation: true
        },
        email_subscription: {
          title: this.$t('UserProfiles_Field_EmailSubscription'),
          defaultValue: false,
          validation: true
        },
        sms_addressable: {
          title: this.$t('UserProfiles_Field_SmsAddressable'),
          defaultValue: false,
          validation: true
        },
        sms_subscription: {
          title: this.$t('UserProfiles_Field_SmsSubscription'),
          defaultValue: false,
          validation: true
        },
        whatsapp_addressable: {
          title: this.$t('UserProfiles_Field_WhatsappAddressable'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        whatsapp_subscription: {
          title: this.$t('UserProfiles_Field_WhatsappSubscription'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        archived: {
          title: this.$t('UserProfiles_Field_Archived'),
          defaultValue: false,
          validation: true
        }
      }

      const attrs = {}
      this.profileAttributes.forEach(item => {
        attrs[`attr_${item.name}`] = {
          title: item.display_name || item.name,
          defaultValue: false,
          validation: true,
          hidden: true
        }
      })

      return { ...columns, ...attrs }
    },
    deviceColumns() {
      const columns = {
        id: {
          title: this.$t('Devices_Field_Id'),
          defaultValue: true,
          validation: true
        },
        profile_id: {
          title: this.$t('Devices_Field_ProfileId'),
          defaultValue: false,
          validation: this.features.profileEnabled
        },
        user_id: {
          title: this.$t('Devices_Field_UserId'),
          defaultValue: true,
          validation:
            this.features.profileEnabled && this.$can('UserPersonalData::Read')
        },
        external_id: {
          title: this.$t('Devices_Field_ExternalId'),
          defaultValue: false,
          validation:
            !this.features.profileEnabled || !this.features.profileIdfybyExtid
        },
        application: {
          title: this.$t('Devices_Field_Application'),
          defaultValue: true,
          validation: true
        },
        type: {
          title: this.$t('Devices_Field_Type'),
          defaultValue: true,
          validation: true
        },
        active: {
          title: this.$t('Devices_Field_Active'),
          defaultValue: false,
          validation: true
        },
        environment: {
          title: this.$t('Devices_Field_Environment'),
          defaultValue: true,
          validation: true
        },
        addressable: {
          title: this.$t('Devices_Field_Addressable'),
          defaultValue: false,
          validation: true
        },
        subscription: {
          title: this.$t('Devices_Field_Subscription'),
          defaultValue: false,
          validation: true
        },
        token: {
          title: this.$t('Devices_Field_Token'),
          defaultValue: false,
          validation: true
        },
        device_model_name: {
          title: this.$t('Devices_Field_DeviceModelName'),
          defaultValue: false,
          validation: this.features.mobile
        },
        browser: {
          title: this.$t('Devices_Field_Browser'),
          defaultValue: false,
          validation: this.features.web
        },
        name: {
          title: this.$t('Devices_Field_Name'),
          defaultValue: false,
          validation: this.features.mobile
        },
        device_id: {
          title: this.$t('Devices_Field_DeviceId'),
          defaultValue: false,
          validation: this.features.mobile
        },
        device_idfv: {
          title: this.$t('Devices_Field_IDFV'),
          defaultValue: false,
          validation: this.features.mobile
        },
        device_idfa: {
          title: this.$t('Devices_Field_IDFA'),
          defaultValue: false,
          validation: this.features.mobile
        },
        device_anid: {
          title: this.$t('Devices_Field_ANID'),
          defaultValue: false,
          validation: this.features.mobile
        },
        device_adid: {
          title: this.$t('Devices_Field_ADID'),
          defaultValue: false,
          validation: this.features.mobile
        },
        ga_id: {
          title: this.$t('Devices_Field_GaID'),
          defaultValue: false,
          validation: true
        },
        create_time: {
          title: this.$t('Devices_Field_CreateTime'),
          defaultValue: false,
          validation: true
        },
        deactivate_time: {
          title: this.$t('Devices_Field_DeactivateTime'),
          defaultValue: false,
          validation: this.features.mobile
        },
        open_time: {
          title: this.$t('Devices_Field_OpenTime'),
          defaultValue: false,
          validation: true
        }
      }

      const attrs = {}
      this.allAttributes.forEach(item => {
        attrs[`attr_${item.name}`] = {
          title: item.display_name || item.name,
          defaultValue: false,
          validation: true,
          hidden: true
        }
      })

      return { ...columns, ...attrs }
    },
    channels() {
      return {
        ...channelLabels,
        inapp: 'CampaignForm_ChannelsSelect_Inapp',
        promotion: 'CampaignForm_ChannelsSelect_Promotion'
      }
    },
    deviceTypeFilter() {
      return _.pickBy(DeviceTypeLabel, (value, key) => {
        if (
          ([
            channelTypes.android_push,
            channelTypes.inapp,
            channelTypes.webhook
          ].includes(parseInt(this.activeTab)) &&
            key === 'android') ||
          ([
            channelTypes.ios_push,
            channelTypes.inapp,
            channelTypes.webhook
          ].includes(parseInt(this.activeTab)) &&
            key === 'ios') ||
          ([
            channelTypes.webhook,
            channelTypes.web_push,
            channelTypes.onsite
          ].includes(parseInt(this.activeTab)) &&
            key === 'web')
        ) {
          return true
        }
      })
    },
    applicationFilter() {
      let filteredApps = _.filter(this.allApplications, app => {
        return (
          (app.type === 'web' &&
            [
              channelTypes.onsite,
              channelTypes.web_push,
              channelTypes.webhook
            ].includes(parseInt(this.activeTab))) ||
          (app.type === 'ios' &&
            [
              channelTypes.inapp,
              channelTypes.ios_push,
              channelTypes.webhook
            ].includes(parseInt(this.activeTab))) ||
          (app.type === 'android' &&
            [
              channelTypes.inapp,
              channelTypes.android_push,
              channelTypes.webhook
            ].includes(parseInt(this.activeTab)))
        )
      })
      let filteredValues = {}
      filteredApps.map(app => {
        filteredValues[app.id] = app.name
      })
      return filteredValues
    },
    pushEnvironments() {
      return _.pickBy(PushEnvironmentLabel, (value, key) => {
        return (
          !(
            [
              channelTypes.android_push,
              channelTypes.inapp,
              channelTypes.ios_push
            ].includes(parseInt(this.activeTab)) &&
            ['safari', 'chrome', 'firefox'].indexOf(key) !== -1
          ) &&
          !(
            [channelTypes.web_push, channelTypes.onsite].includes(
              parseInt(this.activeTab)
            ) && ['production', 'sandbox', 'legacy'].indexOf(key) !== -1
          )
        )
      })
    },
    customerIdEnabled() {
      return $hasFeature('customerIdEnabled')
    }
  },
  methods: {
    getChannelLabel(ch) {
      const types = _.invert(this.channelTypes)
      return this.$t(_.get(this.channels, types[ch]))
    },
    rowClickRoute(row) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: row.id
        }
      })
    },
    rowClickProfileIdRoute(row) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: row.profile_id
        }
      })
    },
    rowClickDeviceIdRoute(row) {
      return projectRoute({
        name: 'Device',
        query: {
          device_id: row.id
        }
      })
    },
    handleClose() {
      this.$emit('update:dialogVisible', false)
    }
  }
}
</script>

<style scoped>
.no-results-found-text {
  font-size: 15px;
  color: #acb0b5;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/RecipientsListDialog.vue